import React, { useEffect, useState } from "react";
import { Alert, Snackbar, Typography } from "@mui/material";
import useStaff from "../../hooks/useStaff";
import useEventoStore from "../../stores/useEventoStore";
import { tipoDeRol } from "../../domain/staff/roles";
import FormularioMiembro from "./FormularioMiembro";
import TablaMiembros from "./TablaMiembros";
import DeleteStaffModal from "../modales/DeleteStaffModal";
import AssignPinModal from "../modales/caja/AssignPinModal";

const ESTADO_FINALIZADO = 4;

const tituloSx = {
  fontFamily: "Bricolage Grotesque",
  color: "#0B0060",
  fontSize: "20px",
  fontWeight: 600,
  mt: 4,
  mb: 2,
};

/* Gestión del staff de un evento: alta, tabla con edición, baja y PIN.
 * Es la misma en el panel de Staff y en el paso de colaboradores del alta;
 * sólo cambian las props.
 *
 * - puedeGestionar: muestra el formulario y las acciones (owner).
 * - ocultarCreador: no lista a quien creó el evento (en el alta). */
const GestionStaff = ({ eventoId, puedeGestionar = false, ocultarCreador = false }) => {
  const [evento, setEvento] = useState(null);
  const [aviso, setAviso] = useState({ open: false, mensaje: "", tipo: "success" });
  const [aEliminar, setAEliminar] = useState(null);
  const [paraPin, setParaPin] = useState(null);
  const [asignandoPin, setAsignandoPin] = useState(false);

  useEffect(() => {
    let cancelado = false;
    useEventoStore.getState().fetchEvento(eventoId).then((data) => {
      if (!cancelado && data?.evento) setEvento(data.evento);
    });
    return () => { cancelado = true; };
  }, [eventoId]);

  // para ocultar al creador hay que saber quién es: hasta tener el evento no
  // se pide la lista (si no, aparece un instante y desaparece)
  const staff = useStaff(ocultarCreador && !evento ? null : eventoId, {
    ocultarUsuarioId: ocultarCreador ? evento?.ownerid ?? null : null,
    filasPorPaginaInicial: ocultarCreador ? 5 : 10,
  });

  const avisar = (mensaje, tipo = "success") => setAviso({ open: true, mensaje, tipo });

  const agregar = async (datos) => {
    const resultado = await staff.agregar(datos);
    if (resultado.ok) avisar("Miembro agregado correctamente");
    return resultado;
  };

  const guardar = async (miembro, cambios) => {
    const resultado = await staff.actualizar(miembro, cambios);
    if (resultado.ok) avisar("Miembro actualizado correctamente");
    else avisar(resultado.error, "error");
    return resultado.ok;
  };

  const asignarPin = async (pin) => {
    setAsignandoPin(true);
    const resultado = await staff.asignarPin(paraPin, pin);
    setAsignandoPin(false);
    if (resultado.ok) {
      setParaPin(null);
      avisar("PIN asignado correctamente");
    } else {
      avisar(resultado.error, "error");
    }
  };

  const finalizado = evento?.id_estados_eventos === ESTADO_FINALIZADO;

  return (
    <>
      {puedeGestionar && (
        <>
          {finalizado && (
            <Alert severity="info" sx={{ mt: 2 }}>
              El evento está finalizado: ya no se pueden agregar miembros.
            </Alert>
          )}
          <FormularioMiembro onAgregar={agregar} deshabilitado={finalizado || !evento} />
        </>
      )}

      <Typography variant="h6" component="h2" sx={tituloSx}>
        Miembros del Staff
      </Typography>

      {staff.error && <Alert severity="error" sx={{ mb: 2 }}>{staff.error}</Alert>}

      <TablaMiembros
        miembros={staff.miembros}
        cargando={staff.cargando}
        total={staff.total}
        pagina={staff.pagina}
        filasPorPagina={staff.filasPorPagina}
        onCambiarPagina={staff.setPagina}
        onCambiarFilas={staff.setFilasPorPagina}
        busquedaInput={staff.busquedaInput}
        onBuscar={staff.setBusquedaInput}
        hayBusqueda={Boolean(staff.busqueda)}
        puedeGestionar={puedeGestionar}
        onGuardar={guardar}
        onEliminar={setAEliminar}
        onPin={setParaPin}
      />

      {aEliminar && (
        <DeleteStaffModal
          isOpen={Boolean(aEliminar)}
          toggle={() => setAEliminar(null)}
          id={aEliminar.id}
          memberType={tipoDeRol(aEliminar.idrol)}
          memberName={aEliminar.nombre || aEliminar.email}
          onSuccess={() => {
            setAEliminar(null);
            avisar("Miembro eliminado correctamente");
            staff.recargar({ silencioso: true });
          }}
          onError={(mensaje) => avisar(`Error al eliminar: ${mensaje || "error desconocido"}`, "error")}
        />
      )}

      <AssignPinModal
        open={Boolean(paraPin)}
        onClose={() => setParaPin(null)}
        onAsignar={asignarPin}
        vendedorNombre={paraPin?.nombre || paraPin?.email || ""}
        tienePin={paraPin?.tienePin || false}
        loading={asignandoPin}
      />

      <Snackbar
        open={aviso.open}
        autoHideDuration={5000}
        onClose={(_, razon) => razon !== "clickaway" && setAviso((a) => ({ ...a, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={aviso.tipo} variant="filled" sx={{ width: "100%", fontFamily: "Bricolage Grotesque" }}>
          {aviso.mensaje}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GestionStaff;
